import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { MediaUpload } from "@/components/MediaUpload";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const CATEGORIES = ["Web Development", "PC Repair", "Automation", "General"];

export default function Blog() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: posts } = useQuery({ queryKey: ["admin-blog"], queryFn: () => api.get("/admin/blog").then((r) => r.data.posts) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-blog"] }); qc.invalidateQueries({ queryKey: ["blog"] }); };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/blog/${p.id}`); refresh(); toast.success("Post deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Blog" subtitle="Write and manage blog posts — published instantly, no redeploy" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setEditing({})} data-testid="new-post-btn"><Plus className="h-4 w-4" /> New Post</Button>
      } />
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono"><tr><th className="text-left p-3">Title</th><th className="text-left p-3">Category</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody>
            {(posts || []).length === 0 ? <tr><td colSpan={4} className="p-8 text-center text-slate-400">No posts yet.</td></tr> :
              (posts || []).map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`post-row-${p.id}`}>
                  <td className="p-3 font-medium text-navy">{p.title}<span className="block text-xs text-slate-400 font-mono">/{p.slug}</span></td>
                  <td className="p-3 text-slate-600">{p.category}</td>
                  <td className="p-3">{p.published ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Published</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> Draft</span>}{p.featured && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Featured</span>}</td>
                  <td className="p-3"><div className="flex gap-1 justify-end">
                    <button title="Edit" onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-post-${p.id}`}><Pencil className="h-4 w-4" /></button>
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="View" className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><ExternalLink className="h-4 w-4" /></a>
                    <button title="Delete" onClick={() => remove(p)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-post-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {editing !== null && <PostDialog post={editing} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} />}
    </div>
  );
}

function PostDialog({ post, onClose, onSaved }) {
  const isEdit = !!post.id;
  const [form, setForm] = useState({
    title: post.title || "", slug: post.slug || "", category: post.category || "Web Development",
    excerpt: post.excerpt || "", content: post.content || "", author: post.author || "TDW Team",
    featuredImage: post.featuredImage || null, published: post.published !== false, featured: !!post.featured,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, slug: form.slug || slugify(form.title) };
    try {
      if (isEdit) await api.put(`/admin/blog/${post.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(isEdit ? "Post updated" : "Post created"); onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{isEdit ? "Edit Post" : "New Post"}</DialogTitle><DialogDescription>Content supports Markdown (headings, bold, lists).</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="post-form">
        <input className={input} placeholder="Title" required value={form.title} onChange={(e) => { set("title", e.target.value); if (!isEdit) set("slug", slugify(e.target.value)); }} data-testid="post-title" />
        <div className="grid grid-cols-2 gap-3">
          <input className={input} placeholder="slug" value={form.slug} onChange={(e) => set("slug", e.target.value)} data-testid="post-slug" />
          <select className={input} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="post-category">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
        </div>
        <input className={input} placeholder="Excerpt (short summary)" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="post-excerpt" />
        <textarea className={`${input} h-auto py-2 font-mono text-xs`} rows={8} placeholder="Content (Markdown supported)" value={form.content} onChange={(e) => set("content", e.target.value)} data-testid="post-content" />
        <MediaUpload label="Featured image (optional)" value={form.featuredImage} onChange={(v) => set("featuredImage", v)} testid="post-image" />
        <div className="flex items-center gap-6 pt-1">
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} data-testid="post-published" /> Published</label>
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="post-featured" /> Featured</label>
        </div>
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="post-save">{isEdit ? "Save Changes" : "Create Post"}</Button>
      </form>
    </DialogContent></Dialog>
  );
}
